import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "../contexts/AuthContext";
import axios from "axios";
import { 
  Trophy, RefreshCw, Loader2, ChevronDown, Crown, Star, Gift, Zap, Share2
} from "lucide-react";
import useCelebration from "../hooks/useCelebration";
import CelebrationOverlay from "../components/CelebrationOverlay";
import SponsorBanner from "../components/SponsorBanner";
import UserProfileCard from "../components/UserProfileCard";
import CricketProBanner from "../components/CricketProBanner";
import { getDisplayName, getDisplayShortName } from "../utils/teamNameMapping";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RankArrow = ({ rankChange, value }) => {
  if (rankChange === "up") return (
    <span className="text-emerald-400 text-xs font-semibold ml-1" data-testid="rank-arrow-up">↑{value}</span>
  );
  if (rankChange === "down") return (
    <span className="text-red-400 text-xs font-semibold ml-1" data-testid="rank-arrow-down">↓{value}</span>
  );
  if (rankChange === "new") return (
    <span className="text-amber-400 text-xs ml-1" data-testid="rank-arrow-new">NEW</span>
  );
  return <span className="text-slate-600 text-xs ml-1">—</span>;
};

export default function LeaderboardPage() {
  const { user, getAuthHeader, dbUser } = useAuth();
  const { winnersData, showCelebration, dismissCelebration } = useCelebration("leaderboard");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [leaderboard, setLeaderboard] = useState([]);
  const [leagues, setLeagues] = useState([]);
  const [selectedLeague, setSelectedLeague] = useState("global");
  const [viewMode, setViewMode] = useState("overall"); // "overall" or "weekly"
  const [currentWeek, setCurrentWeek] = useState(0);
  const [selectedWeek, setSelectedWeek] = useState(null);
  const selectedWeekBtnRef = useRef(null);
  const [weekCaps, setWeekCaps] = useState([]);
  const [gameMode, setGameMode] = useState("season"); // "season" or "matchday"
  const [dailyLeaderboard, setDailyLeaderboard] = useState([]);
  // Match Day specific state
  const [dailyViewMode, setDailyViewMode] = useState("cumulative"); // "cumulative" or "per-match"
  const [completedMatches, setCompletedMatches] = useState([]);
  const [selectedMatchId, setSelectedMatchId] = useState(null);
  const [matchLeaderboard, setMatchLeaderboard] = useState([]);
  const [userMatchRank, setUserMatchRank] = useState(null);
  // User profile modal
  const [selectedProfileUserId, setSelectedProfileUserId] = useState(null);

  // Auto-scroll the selected week button into view when current/selected week changes
  useEffect(() => {
    if (viewMode === "weekly" && selectedWeekBtnRef.current) {
      try {
        selectedWeekBtnRef.current.scrollIntoView({ inline: "center", block: "nearest", behavior: "smooth" });
      } catch (e) {
        selectedWeekBtnRef.current.scrollIntoView({ inline: "center", block: "nearest" });
      }
    }
  }, [currentWeek, selectedWeek, viewMode]);


  const fetchData = async () => {
    try {
      const [leaderboardRes, leaguesRes] = await Promise.all([
        axios.get(`${API}/leaderboard`),
        user ? axios.get(`${API}/leagues/my-leagues`, { headers: getAuthHeader() }).catch(() => ({ data: { leagues: [] } })) : Promise.resolve({ data: { leagues: [] } })
      ]);
      
      const week = leaderboardRes.data.current_week || 0;
      setCurrentWeek(week);
      
      const weekToShow = selectedWeek || week;
      
      if (viewMode === "weekly" && weekToShow > 0) {
        const weeklyRes = await axios.get(`${API}/leaderboard/weekly/${weekToShow}`);
        setLeaderboard(weeklyRes.data.leaderboard || []);
        setWeekCaps(weeklyRes.data.caps || []);
      } else {
        setLeaderboard(leaderboardRes.data.leaderboard || []);
        setWeekCaps([]);
      }
      setLeagues(leaguesRes.data.leagues || []);
    } catch (e) {
      console.error("Failed to fetch leaderboard:", e);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Fetch completed matches with daily results
  const fetchCompletedMatches = async () => {
    try {
      const res = await axios.get(`${API}/daily/matches/with-results`);
      const matches = res.data.matches || [];
      setCompletedMatches(matches);
      // Default to most recent match
      if (matches.length > 0 && !selectedMatchId) {
        setSelectedMatchId(matches[0].match_id);
      }
    } catch (e) {
      console.error("Failed to fetch completed matches:", e);
    }
  };

  // Fetch per-match leaderboard
  const fetchMatchLeaderboard = async (matchId) => {
    if (!matchId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/daily/leaderboard/${matchId}?limit=100`);
      const lb = res.data.leaderboard || [];
      setMatchLeaderboard(lb);
      // Find user's rank in this match
      if (user && dbUser) {
        const userEntry = lb.find(e => e.user_id === dbUser.id);
        setUserMatchRank(userEntry || null);
      }
    } catch (e) {
      console.error("Failed to fetch match leaderboard:", e);
      setMatchLeaderboard([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (gameMode === "matchday") {
      if (dailyViewMode === "cumulative") {
        setLoading(true);
        axios.get(`${API}/daily/leaderboard/cumulative?limit=100`)
          .then(res => setDailyLeaderboard(res.data.leaderboard || []))
          .catch(() => setDailyLeaderboard([]))
          .finally(() => { setLoading(false); setRefreshing(false); });
      } else {
        // Per-match mode - fetch completed matches if not loaded
        if (completedMatches.length === 0) {
          fetchCompletedMatches();
        }
        if (selectedMatchId) {
          fetchMatchLeaderboard(selectedMatchId);
        }
      }
    } else {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, viewMode, selectedWeek, gameMode, dailyViewMode, selectedMatchId]);

  // WhatsApp share for per-match leaderboard
  const handleWhatsAppShare = () => {
    if (!userMatchRank || !selectedMatchId) return;
    const selectedMatch = completedMatches.find(m => m.match_id === selectedMatchId);
    const matchName = selectedMatch ? `${selectedMatch.team1_short || selectedMatch.team1} vs ${selectedMatch.team2_short || selectedMatch.team2}` : "Match Day";
    const referralCode = dbUser?.referral_code || "APL";
    const shareUrl = `${window.location.origin}/auth?ref=${referralCode}`;
    
    const message = `🏏 I ranked #${userMatchRank.rank} in today's APL Match Day (${matchName}) with ${userMatchRank.points} points!\n\nJoin me on Apna Premier League and compete. Use my referral code *${referralCode}* to get 5 Cr bonus!\n\n👉 ${shareUrl}`;
    
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const fetchLeagueLeaderboard = async (leagueId) => {
    if (leagueId === "global") {
      fetchData();
      return;
    }
    
    setLoading(true);
    try {
      const res = await axios.get(`${API}/leagues/${leagueId}`, { headers: getAuthHeader() });
      setLeaderboard(res.data.leaderboard || []);
    } catch (e) {
      console.error("Failed to fetch league leaderboard:", e);
    } finally {
      setLoading(false);
    }
  };

  const handleLeagueChange = (e) => {
    const leagueId = e.target.value;
    setSelectedLeague(leagueId);
    fetchLeagueLeaderboard(leagueId);
  };

  const handleRefresh = () => {
    setRefreshing(true);
    if (selectedLeague === "global") {
      fetchData();
    } else {
      fetchLeagueLeaderboard(selectedLeague);
    }
  };

  const getRankStyle = (rank) => {
    if (rank === 1) return "bg-yellow-500/20 border-yellow-500/50";
    if (rank === 2) return "bg-slate-400/20 border-slate-400/50";
    if (rank === 3) return "bg-orange-500/20 border-orange-500/50";
    return "bg-slate-800/50 border-slate-700/50";
  };

  const getRankBadge = (rank) => {
    if (rank === 1) return "bg-yellow-500 text-black";
    if (rank === 2) return "bg-slate-400 text-black";
    if (rank === 3) return "bg-orange-500 text-black";
    return "bg-slate-700 text-slate-300";
  };

  if (loading && !refreshing) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="animate-spin text-emerald-400" size={48} />
      </div>
    );
  }

  const selectedLeagueName = selectedLeague === "global" 
    ? "Global Leaderboard" 
    : leagues.find(l => l.id === selectedLeague)?.name || "League";

  return (
    <div>
    <div className="space-y-6" data-testid="leaderboard-page">
      {showCelebration && winnersData && (
        <CelebrationOverlay winnersData={winnersData} pageType="leaderboard" onDismiss={dismissCelebration} />
      )}
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-white">Leaderboard</h1>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="flex items-center gap-2 px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-slate-300 hover:bg-slate-700 transition-all disabled:opacity-50"
          data-testid="refresh-btn"
        >
          <RefreshCw className={`${refreshing ? 'animate-spin' : ''}`} size={16} />
          Refresh Data
        </button>
      </div>

      {/* Cricket Pro activation banner (free cricket users only) */}
      <CricketProBanner variant="banner" storageKey="cricket_pro_banner_dismissed_at" />

      {/* Season / Match Day Toggle - Compact Pill Style */}
      <div className="flex bg-slate-800/60 border border-slate-700/50 rounded-full p-0.5" data-testid="game-mode-toggle">
        <button
          onClick={() => setGameMode("season")}
          className={`flex-1 py-1.5 px-3 rounded-full font-medium text-xs transition-all flex items-center justify-center gap-1.5 ${
            gameMode === "season"
              ? "bg-emerald-500 text-white"
              : "text-slate-400 hover:text-white"
          }`}
          data-testid="season-mode-btn"
        >
          <Trophy size={14} /> Season
        </button>
        <button
          onClick={() => setGameMode("matchday")}
          className={`flex-1 py-1.5 px-3 rounded-full font-medium text-xs transition-all flex items-center justify-center gap-1.5 ${
            gameMode === "matchday"
              ? "bg-amber-500 text-black"
              : "text-slate-400 hover:text-white"
          }`}
          data-testid="matchday-mode-btn"
        >
          <Zap size={14} /> Match Day
        </button>
      </div>

      {/* Match Day Leaderboard */}
      {gameMode === "matchday" ? (
        <div className="space-y-4">
          {/* Cumulative / Per-Match Toggle */}
          <div className="flex bg-slate-800/50 border border-slate-700/50 rounded-xl p-1" data-testid="daily-view-toggle">
            <button
              onClick={() => setDailyViewMode("cumulative")}
              className={`flex-1 py-2 px-3 rounded-lg font-medium text-sm transition-all ${
                dailyViewMode === "cumulative"
                  ? "bg-amber-500 text-black"
                  : "text-slate-400 hover:text-white"
              }`}
              data-testid="cumulative-btn"
            >
              All-Time
            </button>
            <button
              onClick={() => { setDailyViewMode("per-match"); fetchCompletedMatches(); }}
              className={`flex-1 py-2 px-3 rounded-lg font-medium text-sm transition-all ${
                dailyViewMode === "per-match"
                  ? "bg-amber-500 text-black"
                  : "text-slate-400 hover:text-white"
              }`}
              data-testid="per-match-btn"
            >
              Per Match
            </button>
          </div>

          {dailyViewMode === "per-match" ? (
            <>
              {/* Match Selector Dropdown */}
              {completedMatches.length > 0 && (
                <div className="relative">
                  <select
                    value={selectedMatchId || ""}
                    onChange={(e) => setSelectedMatchId(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-xl text-white appearance-none focus:outline-none focus:border-amber-500 pr-10"
                    data-testid="match-selector"
                  >
                    {completedMatches.map(match => (
                      <option key={match.match_id} value={match.match_id}>
                        {match.team1_short || match.team1} vs {match.team2_short || match.team2} ({match.date}) - {match.participants} teams
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={20} />
                </div>
              )}

              {/* Your Rank Card - Screenshot friendly */}
              {userMatchRank && (
                <div className="bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/40 rounded-xl p-4" data-testid="your-rank-card">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="text-center">
                        <p className="text-3xl font-bold text-amber-400">#{userMatchRank.rank}</p>
                        <p className="text-xs text-slate-400">Your Rank</p>
                      </div>
                      <div className="h-10 w-px bg-slate-600"></div>
                      <div className="text-center">
                        <p className="text-2xl font-bold text-white">{userMatchRank.points}</p>
                        <p className="text-xs text-slate-400">Points</p>
                      </div>
                      <div className="hidden sm:block">
                        <p className="text-sm text-white font-medium">{userMatchRank.team_name || userMatchRank.display_name}</p>
                        <p className="text-xs text-slate-400">Captain: {userMatchRank.captain_name}</p>
                      </div>
                    </div>
                    <button
                      onClick={handleWhatsAppShare}
                      className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-500 text-white rounded-lg font-medium transition-all"
                      data-testid="whatsapp-share-btn"
                    >
                      <Share2 size={16} />
                      <span className="hidden sm:inline">Share on WhatsApp</span>
                      <span className="sm:hidden">Share</span>
                    </button>
                  </div>
                </div>
              )}

              {/* Per-Match Leaderboard */}
              {matchLeaderboard.length === 0 ? (
                <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-8 text-center">
                  <Zap className="mx-auto text-slate-600 mb-4" size={48} />
                  <p className="text-slate-400 mb-2">No results for this match yet</p>
                  <p className="text-slate-500 text-sm">Select a completed match to view rankings</p>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="hidden sm:grid grid-cols-12 gap-2 px-4 py-2 text-xs text-slate-400 font-medium uppercase tracking-wider">
                    <div className="col-span-1">Rank</div>
                    <div className="col-span-5">Team</div>
                    <div className="col-span-3">Captain</div>
                    <div className="col-span-3 text-right">Points</div>
                  </div>
                  {matchLeaderboard.map((entry, index) => {
                    const rank = entry.rank || index + 1;
                    const isCurrentUser = dbUser && entry.user_id === dbUser.id;
                    return (
                      <div 
                        key={entry.user_id}
                        className={`p-3 sm:p-4 rounded-xl border transition-all ${isCurrentUser ? 'bg-amber-500/20 border-amber-500/50 ring-2 ring-amber-500/30' : getRankStyle(rank)}`}
                        data-testid={`match-lb-row-${rank}`}
                      >
                        {/* Mobile */}
                        <div className="sm:hidden flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className={`w-7 h-7 flex items-center justify-center rounded-full font-bold text-xs ${getRankBadge(rank)}`}>
                              {rank}
                            </span>
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center text-white font-bold text-xs">
                              {(entry.team_name || entry.display_name || "?")[0]?.toUpperCase()}
                            </div>
                            <div>
                              <span className="text-white font-medium text-sm truncate max-w-[100px] block">{entry.team_name || entry.display_name}</span>
                              <span className="text-xs text-slate-500">C: {entry.captain_name}</span>
                            </div>
                          </div>
                          <span className="text-amber-400 font-mono font-bold text-lg">{entry.points}</span>
                        </div>
                        {/* Desktop */}
                        <div className="hidden sm:grid grid-cols-12 gap-2 items-center">
                          <div className="col-span-1">
                            <span className={`w-8 h-8 flex items-center justify-center rounded-full font-bold text-sm ${getRankBadge(rank)}`}>{rank}</span>
                          </div>
                          <div className="col-span-5 flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center text-white font-bold text-sm">
                              {(entry.team_name || entry.display_name || "?")[0]?.toUpperCase()}
                            </div>
                            <div>
                              <p className="text-white font-medium truncate">{entry.team_name || entry.display_name}</p>
                              {isCurrentUser && <span className="text-xs text-amber-400">You</span>}
                            </div>
                          </div>
                          <div className="col-span-3 text-sm text-slate-400">{entry.captain_name}</div>
                          <div className="col-span-3 text-right">
                            <span className="text-amber-400 font-mono font-bold text-lg">{entry.points}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {/* Sponsor Footer for Per-Match Leaderboard */}
                  {(() => {
                    const selectedMatch = completedMatches.find(m => m.match_id === selectedMatchId);
                    return selectedMatch?.sponsor ? (
                      <SponsorBanner sponsor={selectedMatch.sponsor} variant="footer" />
                    ) : null;
                  })()}
                </div>
              )}
            </>
          ) : (
            <>
              {/* Cumulative Leaderboard - existing code */}
              <div className="flex items-center gap-3">
                <Zap className="text-amber-400" size={24} />
                <div>
                  <h2 className="text-lg font-semibold text-white">Match Day Leaderboard</h2>
                  <p className="text-slate-400 text-sm">Cumulative daily fantasy standings</p>
                </div>
              </div>

              {dailyLeaderboard.length === 0 ? (
                <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-8 text-center">
                  <Zap className="mx-auto text-slate-600 mb-4" size={48} />
                  <p className="text-slate-400 mb-2">No Match Day rankings yet</p>
                  <p className="text-slate-500 text-sm">Be the first to build a Match Day team!</p>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="hidden sm:grid grid-cols-12 gap-2 px-4 py-2 text-xs text-slate-400 font-medium uppercase tracking-wider">
                    <div className="col-span-1">Rank</div>
                    <div className="col-span-6">Team</div>
                    <div className="col-span-2 text-center">Matches</div>
                    <div className="col-span-3 text-right">Points</div>
                  </div>
                  {dailyLeaderboard.map((entry, index) => {
                    const rank = entry.rank || index + 1;
                    return (
                      <div 
                        key={entry.user_id}
                        className={`p-3 sm:p-4 rounded-xl border transition-all ${getRankStyle(rank)}`}
                        data-testid={`daily-lb-row-${rank}`}
                      >
                        {/* Mobile */}
                        <div className="sm:hidden flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className={`w-7 h-7 flex items-center justify-center rounded-full font-bold text-xs ${getRankBadge(rank)}`}>
                              {rank}
                            </span>
                            <RankArrow rankChange={entry.rank_change} value={entry.rank_change_value} />
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center text-white font-bold text-xs">
                              {(entry.team_name || entry.display_name || "?")[0]?.toUpperCase()}
                            </div>
                            <div>
                              <span className="text-white font-medium text-sm truncate max-w-[120px] block">{entry.team_name || entry.display_name}</span>
                              <span className="text-xs text-slate-500">{entry.daily_matches_played}m played</span>
                            </div>
                          </div>
                          <span className="text-amber-400 font-mono font-bold text-lg">{entry.daily_total_points}</span>
                        </div>
                        {/* Desktop */}
                        <div className="hidden sm:grid grid-cols-12 gap-2 items-center">
                          <div className="col-span-1">
                            <span className={`w-8 h-8 flex items-center justify-center rounded-full font-bold text-sm ${getRankBadge(rank)}`}>{rank}</span>
                            <RankArrow rankChange={entry.rank_change} value={entry.rank_change_value} />
                          </div>
                          <div className="col-span-6 flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center text-white font-bold text-sm">
                              {(entry.team_name || entry.display_name || "?")[0]?.toUpperCase()}
                            </div>
                            <p className="text-white font-medium truncate">{entry.team_name || entry.display_name}</p>
                          </div>
                          <div className="col-span-2 text-center text-sm text-slate-400">{entry.daily_matches_played}</div>
                          <div className="col-span-3 text-right">
                            <span className="text-amber-400 font-mono font-bold text-lg">{entry.daily_total_points}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      ) : (
      <>
      {/* View Mode Tabs + League Filter - Same Row */}
      <div className="flex items-center gap-2">
        <div className="flex bg-slate-800/60 border border-slate-700/50 rounded-full p-0.5 flex-1">
          <button
            onClick={() => setViewMode("overall")}
            className={`flex-1 py-1 px-2 rounded-full font-medium text-xs transition-all ${
              viewMode === "overall" 
                ? "bg-emerald-500 text-white" 
                : "text-slate-400 hover:text-white"
            }`}
            data-testid="overall-tab"
          >
            Overall
          </button>
          <button
            onClick={() => setViewMode("weekly")}
            className={`flex-1 py-1 px-2 rounded-full font-medium text-xs transition-all ${
              viewMode === "weekly" 
                ? "bg-emerald-500 text-white" 
                : "text-slate-400 hover:text-white"
            }`}
            data-testid="weekly-tab"
          >
            Weekly
          </button>
        </div>
        
        <select
          value={selectedLeague}
          onChange={handleLeagueChange}
          className="pl-2 pr-2 py-1.5 bg-slate-800 border border-slate-700 rounded-lg text-white text-xs focus:outline-none focus:border-emerald-500"
          data-testid="league-filter"
        >
          <option value="global">Global</option>
          {leagues.map(league => (
            <option key={league.id} value={league.id}>{league.name}</option>
          ))}
        </select>
      </div>

      {/* Week Selector (only in weekly mode) */}
      {viewMode === "weekly" && currentWeek > 0 && (
        <div
          className="flex items-center gap-2 overflow-x-auto scrollbar-hide flex-nowrap snap-x snap-mandatory -mx-4 px-4"
          style={{ WebkitOverflowScrolling: "touch" }}
          data-testid="week-selector"
        >
          {Array.from({ length: currentWeek }, (_, i) => i + 1).map(w => {
            const isActive = (selectedWeek || currentWeek) === w;
            return (
              <button
                key={w}
                ref={isActive ? selectedWeekBtnRef : null}
                onClick={() => setSelectedWeek(w)}
                className={`flex-shrink-0 snap-start px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  isActive
                    ? "bg-emerald-500 text-white"
                    : "bg-slate-800 text-slate-400 hover:text-white border border-slate-700"
                }`}
                data-testid={`week-btn-${w}`}
              >
                Week {w}
              </button>
            );
          })}
        </div>
      )}

      {/* Cap Winners Banner (weekly mode only) */}
      {viewMode === "weekly" && weekCaps.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="cap-winners-banner">
          {weekCaps.filter(c => c.cap_type === "orange").map((cap, i) => (
            <div key={`orange-${i}`} className="bg-gradient-to-r from-orange-500/20 to-yellow-500/10 border border-orange-500/40 rounded-xl p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-orange-500/30 flex items-center justify-center text-lg">🟠</div>
              <div>
                <p className="text-orange-400 font-semibold text-sm">Orange Cap</p>
                <p className="text-white font-medium">{cap.player_name}</p>
                <p className="text-orange-300 text-xs">{cap.stat_value} runs this week</p>
              </div>
            </div>
          ))}
          {weekCaps.filter(c => c.cap_type === "purple").map((cap, i) => (
            <div key={`purple-${i}`} className="bg-gradient-to-r from-purple-500/20 to-pink-500/10 border border-purple-500/40 rounded-xl p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-purple-500/30 flex items-center justify-center text-lg">🟣</div>
              <div>
                <p className="text-purple-400 font-semibold text-sm">Purple Cap</p>
                <p className="text-white font-medium">{cap.player_name}</p>
                <p className="text-purple-300 text-xs">{cap.stat_value} wickets this week</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Leaderboard Title */}
      <div className="flex items-center gap-2">
        <Trophy className="text-emerald-400" size={20} />
        <div>
          <h2 className="text-base font-semibold text-white">
            {selectedLeagueName}
          </h2>
          <p className="text-slate-400 text-xs">
            {viewMode === "weekly" 
              ? `Week ${selectedWeek || currentWeek} Standings` 
              : "Overall Standings"}
          </p>
        </div>
      </div>

      {/* Leaderboard Table */}
      {leaderboard.length === 0 ? (
        <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-8 text-center">
          <Trophy className="mx-auto text-slate-600 mb-4" size={48} />
          <p className="text-slate-400 mb-2">No rankings yet</p>
          <p className="text-slate-500 text-sm">
            Rankings will appear after the first match starts
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {/* Table Header */}
          <div className="hidden sm:grid grid-cols-12 gap-2 px-4 py-2 text-xs text-slate-400 font-medium uppercase tracking-wider">
            <div className="col-span-1">Rank</div>
            <div className="col-span-5">Team</div>
            <div className="col-span-2 text-center">Bonus</div>
            <div className="col-span-2 text-center">Predictor</div>
            <div className="col-span-2 text-right">
              {viewMode === "weekly" ? "Week Pts" : "Total Pts"}
            </div>
          </div>

          {/* Leaderboard Rows */}
          {leaderboard.map((entry, index) => {
            const rank = entry.rank || index + 1;
            return (
              <div 
                key={entry.id || index}
                className={`p-3 sm:p-4 rounded-xl border transition-all cursor-pointer hover:scale-[1.01] ${getRankStyle(rank)}`}
                onClick={() => setSelectedProfileUserId(entry.id)}
                data-testid={`leaderboard-row-${rank}`}
              >
                {/* Mobile Layout */}
                <div className="sm:hidden">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className={`w-7 h-7 flex items-center justify-center rounded-full font-bold text-xs ${getRankBadge(rank)}`}>
                        {rank}
                      </span>
                      {viewMode === "weekly" && <RankArrow rankChange={entry.rank_change} value={entry.rank_change_value} />}
                      {entry.photo_url && entry.photo_url.startsWith('data:image') ? (
                        <img 
                          src={entry.photo_url} 
                          alt="" 
                          className="w-8 h-8 rounded-full object-cover border-2 border-emerald-500/50"
                        />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-emerald-500 to-cyan-500 flex items-center justify-center text-white font-bold text-xs">
                          {(entry.team_name || entry.display_name || "?")[0]?.toUpperCase()}
                        </div>
                      )}
                      <span className="text-white font-medium text-sm truncate max-w-[120px]">
                        {entry.team_name || entry.display_name || "Unknown"}
                        {entry.is_tester && <span className="ml-1 text-cyan-400" title="Tester account">&#129514;</span>}
                      </span>
                    </div>
                    <span className="text-emerald-400 font-mono font-bold text-lg">
                      {viewMode === "weekly" 
                        ? (entry.weekly_points || 0)
                        : (entry.total_points || 0)}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-500/20 text-emerald-400 rounded-full">
                      <Gift size={10} />
                      {entry.bonus_points || (entry.referral_count || 0) * 25}
                    </span>
                    {(viewMode === "weekly" ? (entry.weekly_prediction_points || 0) : (entry.prediction_points || 0)) > 0 && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-500/20 text-red-400 rounded-full" data-testid={`pred-pts-mobile-${rank}`}>
                        {viewMode === "weekly" ? (entry.weekly_prediction_points || 0) : (entry.prediction_points || 0)}
                      </span>
                    )}
                    {viewMode === "weekly" && entry.cap_bonuses && entry.cap_bonuses.length > 0 && (
                      <>
                        {entry.cap_bonuses.includes("orange") && (
                          <span className="px-1.5 py-0.5 bg-orange-500/20 text-orange-400 rounded">🟠</span>
                        )}
                        {entry.cap_bonuses.includes("purple") && (
                          <span className="px-1.5 py-0.5 bg-purple-500/20 text-purple-400 rounded">🟣</span>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {/* Desktop Layout */}
                <div className="hidden sm:grid grid-cols-12 gap-2 items-center">
                  {/* Rank */}
                  <div className="col-span-1">
                    <span className={`w-8 h-8 flex items-center justify-center rounded-full font-bold text-sm ${getRankBadge(rank)}`}>
                      {rank}
                    </span>
                    {viewMode === "weekly" && <RankArrow rankChange={entry.rank_change} value={entry.rank_change_value} />}
                  </div>

                  {/* Team Info */}
                  <div className="col-span-5 flex items-center gap-3">
                    {entry.photo_url && entry.photo_url.startsWith('data:image') ? (
                      <img 
                        src={entry.photo_url} 
                        alt="" 
                        className="w-10 h-10 rounded-full object-cover border-2 border-emerald-500/50 flex-shrink-0"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-500 to-cyan-500 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                        {(entry.team_name || entry.display_name || "?")[0]?.toUpperCase()}
                      </div>
                    )}
                    <div className="min-w-0">
                      <p className="text-white font-medium truncate">
                        {entry.team_name || entry.display_name || "Unknown Team"}
                        {entry.is_tester && <span className="ml-1 text-cyan-400" title="Tester account">&#129514;</span>}
                      </p>
                      {rank <= 3 && (
                        <div className="flex items-center gap-1 text-xs text-slate-400">
                          {rank === 1 && <Crown className="text-yellow-500" size={12} />}
                          {rank === 2 && <Star className="text-slate-400" size={12} />}
                          {rank === 3 && <Star className="text-orange-500" size={12} />}
                          <span>
                            {rank === 1 ? "Leader" : rank === 2 ? "2nd Place" : "3rd Place"}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Bonus Points */}
                  <div className="col-span-2 text-center">
                    <div className="flex flex-col items-center gap-1">
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-500/20 text-emerald-400 rounded-full text-sm">
                        <Gift size={12} />
                        {entry.bonus_points || (entry.referral_count || 0) * 25}
                      </span>
                      {viewMode === "weekly" && entry.cap_bonuses && entry.cap_bonuses.length > 0 && (
                        <div className="flex gap-1">
                          {entry.cap_bonuses.includes("orange") && (
                            <span className="text-xs px-1.5 py-0.5 bg-orange-500/20 text-orange-400 rounded" title="Orange Cap Bonus +25">🟠+25</span>
                          )}
                          {entry.cap_bonuses.includes("purple") && (
                            <span className="text-xs px-1.5 py-0.5 bg-purple-500/20 text-purple-400 rounded" title="Purple Cap Bonus +25">🟣+25</span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Predictor Points */}
                  <div className="col-span-2 text-center">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-sm ${
                      (viewMode === "weekly" ? (entry.weekly_prediction_points || 0) : (entry.prediction_points || 0)) > 0
                        ? "bg-red-500/20 text-red-400 font-medium"
                        : "text-slate-600"
                    }`} data-testid={`pred-pts-desktop-${rank}`}>
                      {viewMode === "weekly" ? (entry.weekly_prediction_points || 0) : (entry.prediction_points || 0)}
                    </span>
                  </div>

                  {/* Total Points */}
                  <div className="col-span-2 text-right">
                    <span className="text-emerald-400 font-mono font-bold text-lg">
                      {viewMode === "weekly" 
                        ? (entry.weekly_points || 0)
                        : (entry.total_points || 0)}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Points Info */}
      <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4 space-y-2">
        <h3 className="text-sm font-medium text-white">How Points Are Calculated</h3>
        <p className="text-xs text-slate-400">
          Points are calculated based on your team's performance in each match. 
          Captain gets <span className="text-yellow-400">2x</span> points and 
          Vice-Captain gets <span className="text-cyan-400">1.5x</span> points.
        </p>
        <p className="text-xs text-slate-400">
          Bonus points are awarded for referrals (25 points per successful referral, up to 3 referrals).
        </p>
        <p className="text-xs text-slate-400">
          <span className="text-orange-400">Orange Cap</span> (most runs) and <span className="text-purple-400">Purple Cap</span> (most wickets) winners earn +25 bonus points each week for users who have them in their team.
        </p>
        <p className="text-xs text-slate-400">
          <span className="text-red-400">Predictor points</span> are earned by correctly predicting match outcomes (5-25 pts per correct prediction). These are shown separately in <span className="text-red-400">red</span>.
        </p>
        <p className="text-xs text-slate-400">
          Weekly points include match performance + cap bonuses (excludes referral bonus).
        </p>
      </div>
      </>
      )}
      
      {/* User Profile Modal */}
      {selectedProfileUserId && (
        <UserProfileCard 
          userId={selectedProfileUserId} 
          onClose={() => setSelectedProfileUserId(null)} 
        />
      )}
    </div>
    </div>
  );
}
